import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  APP_NAME,
  DEFAULT_PARTNER_SLUG,
  usePartnerStore,
  type PartnerConfig,
} from '@/stores/partner.store';

/**
 * The privacy policy and terms of service, at /privacy and /terms.
 *
 * Whose they are is the server's answer (`/api/v1/partner`): Faxter Ltd on
 * Faxter's own instance, the partner's company on theirs. The instance's
 * country picks the law — UK GDPR and England and Wales for GB, the Nigeria
 * Data Protection Act and Nigerian law otherwise. A partner that has not given
 * its company name, number and address gets the pages marked as a draft.
 */

const UPDATED = '27 October 2026';

interface Entity {
  name: string;
  registration: string | null;
  address: string | null;
  icoNumber: string | null;
  email: string | null;
  complete: boolean;
  uk: boolean;
}

function entityFor(partner: PartnerConfig | null): Entity {
  const uk = (partner?.default_country ?? '').toUpperCase() === 'GB';
  if (!partner || partner.slug === DEFAULT_PARTNER_SLUG) {
    return {
      name: 'Faxter Ltd',
      registration: 'RC 1677701',
      address: 'Suite 73 Dumec Plaza, Area 1, Abuja',
      icoNumber: null,
      email: partner?.support_email || 'clerk@faxter.com',
      complete: true,
      uk,
    };
  }
  return {
    name: partner.legal_name || partner.name,
    registration: partner.company_number
      ? uk
        ? `company number ${partner.company_number}`
        : `RC ${partner.company_number}`
      : null,
    address: partner.address || null,
    icoNumber: partner.ico_number || null,
    email: partner.support_email || null,
    complete: Boolean(partner.legal_name && partner.company_number && partner.address),
    uk,
  };
}

function useEntity(): Entity | null {
  const { config, loaded, fetchConfig } = usePartnerStore();
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  return loaded ? entityFor(config) : null;
}

function Contact({ entity }: { entity: Entity }) {
  return entity.email ? (
    <a href={`mailto:${entity.email}`}>{entity.email}</a>
  ) : (
    <>the support channel in your account</>
  );
}

function Layout({
  title,
  entity,
  children,
}: {
  title: string;
  entity: Entity | null;
  children: (e: Entity) => ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} — ${APP_NAME}`;
  }, [title]);

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" className="text-lg font-semibold text-ink">
            {APP_NAME}
          </Link>
          <Link to="/login" className="text-sm text-ink-2 hover:text-ink">
            Log in
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12 sm:py-16 [&_a]:text-brand [&_a]:underline [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-relaxed [&_li]:text-ink-2 [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-ink-2 [&_strong]:text-ink [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
        <h1 className="mb-2 text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="mb-8 text-sm text-ink-3">Effective {UPDATED}</p>
        {entity === null ? (
          <div className="space-y-3" aria-hidden>
            <div className="h-4 w-3/4 animate-pulse rounded bg-line" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-line" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-line" />
          </div>
        ) : (
          <>
            {!entity.complete && (
              <div className="mb-8 rounded-lg border border-line bg-surface p-4 text-sm">
                <strong>Draft for review.</strong> This document is a working draft and is pending
                review by legal counsel. It is not yet a binding agreement.
              </div>
            )}
            {children(entity)}
          </>
        )}
      </main>
    </div>
  );
}

function WhoWeAre({ e }: { e: Entity }) {
  return (
    <>
      <strong>{e.name}</strong>
      {e.registration && (
        <>
          , a company registered in {e.uk ? 'England and Wales' : 'Nigeria'} with {e.registration}
        </>
      )}
      {e.address && <>, whose registered office is at {e.address}</>}
    </>
  );
}

function ContactBlock({ e }: { e: Entity }) {
  return (
    <p>
      {e.name}
      {e.address && (
        <>
          <br />
          {e.address}
        </>
      )}
      {e.email && (
        <>
          <br />
          <Contact entity={e} />
        </>
      )}
    </p>
  );
}

export function PrivacyPage() {
  const entity = useEntity();
  return (
    <Layout title="Privacy Policy" entity={entity}>
      {(e) => {
        const law = e.uk ? 'the UK GDPR' : 'the NDPA';
        return (
          <>
            <p>
              {APP_NAME} is an AI support desk: it answers your customers on WhatsApp, web chat and
              phone, sorts every conversation, and hands what needs a person to your team. This
              policy explains what personal data we handle to do that, why, how we protect it, and
              the rights people have over it.
            </p>

            <h2>1. Who we are</h2>
            <p>
              {APP_NAME} is provided by <WhoWeAre e={e} /> (&ldquo;we&rdquo;, &ldquo;us&rdquo;). For
              the personal data of people who use {APP_NAME} to run their support (account holders
              and their team) we are the <strong>data controller</strong> under{' '}
              {e.uk
                ? 'the UK General Data Protection Regulation (“UK GDPR”) and the Data Protection Act 2018'
                : 'the Nigeria Data Protection Act 2023 (“NDPA”)'}
              .
              {e.uk && e.icoNumber && (
                <>
                  {' '}
                  We are registered with the Information Commissioner&rsquo;s Office under number{' '}
                  {e.icoNumber}.
                </>
              )}{' '}
              Questions can be sent to us through <Contact entity={e} />.
            </p>

            <h2>2. Your customers&rsquo; conversations</h2>
            <p>
              When a business uses {APP_NAME}, the messages, calls and details of its customers
              belong to that business. For that data the business is the controller and we are its{' '}
              <strong>processor</strong>: we handle it only to provide the service, under the data
              processing terms in our <Link to="/terms">Terms of Service</Link>. If you are a
              customer of a business that uses {APP_NAME}, please contact that business about your
              data; we will help them respond.
            </p>

            <h2>3. What we collect</h2>
            <ul>
              <li>
                <strong>Account data</strong>: name, email address, phone number, role and login
                credentials for you and your team.
              </li>
              <li>
                <strong>Conversations</strong>: messages, attachments and voice notes exchanged on
                the channels you connect, with transcripts we make of voice notes, and call details
                (and recordings, where you switch them on).
              </li>
              <li>
                <strong>Customer and order details</strong>: the names, contact details and order or
                payment status {APP_NAME} looks up in the systems you connect, so it can answer
                questions about them.
              </li>
              <li>
                <strong>Knowledge</strong>: the documents and answers you give {APP_NAME} to work
                from.
              </li>
              <li>
                <strong>Usage and technical data</strong>: device, browser, IP address and how the
                product is used, to run, secure and improve it.
              </li>
            </ul>

            <h2>4. How we use data, and our lawful basis</h2>
            <p>Under {law} we rely on:</p>
            <ul>
              <li>
                <strong>Contract</strong>: to provide the service you signed up for.
              </li>
              <li>
                <strong>Legal obligation</strong>: to keep the records the law requires and respond
                to lawful requests.
              </li>
              <li>
                <strong>Legitimate interests</strong>: to secure, maintain and improve the product
                and prevent misuse, where those interests are not outweighed by people&rsquo;s
                rights.
              </li>
              <li>
                <strong>Consent</strong>: where the law requires it. Consent can be withdrawn at any
                time.
              </li>
            </ul>

            <h2>5. AI and automated replies</h2>
            <p>
              {APP_NAME} uses AI models to draft and send replies, summarise conversations and
              decide which ones need a person. Your team can see every conversation and take over at
              any time, and {APP_NAME} hands over whenever a customer asks for a person. We do not
              make decisions about anyone based solely on automated processing that have legal or
              similarly significant effects.{' '}
              <strong>
                We do not sell data, and we do not use your conversations to train third-party AI
                models.
              </strong>
            </p>

            <h2>6. Who we share data with</h2>
            <ul>
              <li>
                <strong>Service providers (processors)</strong>: hosting, messaging and telephony,
                AI and email providers, acting only on our instructions under written contracts.
              </li>
              <li>
                <strong>The systems you connect</strong>, such as your shop or payment provider,
                when you ask {APP_NAME} to look something up there.
              </li>
              <li>
                <strong>Authorities</strong>, where the law requires it, and professional advisers
                or the parties to a sale of our business, under suitable safeguards.
              </li>
            </ul>

            <h2>7. International transfers</h2>
            <p>
              {e.uk
                ? 'Some of our providers process data outside the UK. Where they do, the destination is covered by UK adequacy regulations, or we use the International Data Transfer Agreement or the UK Addendum to the EU Standard Contractual Clauses, with any additional measures the transfer needs.'
                : 'Some of our providers process data outside Nigeria. Where data is transferred abroad, we take the steps the NDPA requires to keep it adequately protected, including contractual safeguards with the recipients.'}
            </p>

            <h2>8. How long we keep it</h2>
            <p>
              We keep account data while your account is open and for as long afterwards as the law
              requires. Conversations are kept for as long as your account needs them; when your
              account closes we delete them, or return them if you ask, unless the law requires us
              to keep them.
            </p>

            <h2>9. Security</h2>
            <p>
              We encrypt data in transit and at rest, keep each business&rsquo;s data separate from
              every other&rsquo;s, and limit access to those who need it.
            </p>

            <h2>10. Your rights</h2>
            <p>
              Under {law} you have the right to access the personal data we hold about you, to have
              it corrected or erased where the law allows, to restrict or object to processing, to
              receive it in a portable format, and to withdraw consent where we rely on it. Contact
              us through <Contact entity={e} />.{' '}
              {e.uk ? (
                <>
                  We will respond within one month. You can also complain to the Information
                  Commissioner&rsquo;s Office at{' '}
                  <a href="https://ico.org.uk/make-a-complaint/">ico.org.uk</a> or on 0303 123 1113.
                </>
              ) : (
                <>You can also complain to the Nigeria Data Protection Commission.</>
              )}
            </p>

            <h2>11. Cookies</h2>
            <p>
              We use only the cookies and local storage that are strictly necessary to keep you
              signed in and the service secure. We do not use advertising or third-party tracking
              cookies.
            </p>

            <h2>12. Changes to this policy</h2>
            <p>
              If we make significant changes, we will update the effective date above and, where
              appropriate, let you know.
            </p>

            <h2>13. Contact us</h2>
            <ContactBlock e={e} />
          </>
        );
      }}
    </Layout>
  );
}

export function TermsPage() {
  const entity = useEntity();
  return (
    <Layout title="Terms of Service" entity={entity}>
      {(e) => (
        <>
          <h2>1. Agreement to these terms</h2>
          <p>
            These Terms of Service (the &ldquo;Terms&rdquo;) are a binding agreement between you, or
            the business you represent (&ldquo;you&rdquo;), and <WhoWeAre e={e} />{' '}
            (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using {APP_NAME} you
            accept these Terms. If you accept them for a business, you confirm you have authority to
            bind it. {APP_NAME} is for business use only.
          </p>

          <h2>2. The service</h2>
          <p>
            {APP_NAME} answers your customers on the channels you connect, using AI and the
            knowledge you give it, and hands conversations to your team when they need a person. AI
            replies can be wrong. You are responsible for the knowledge and instructions you give{' '}
            {APP_NAME}, for supervising its replies, and for what your business promises its
            customers.
          </p>

          <h2>3. Your account</h2>
          <p>
            You must be at least 18. You are responsible for your team&rsquo;s logins and for all
            activity under your account. Tell us promptly through <Contact entity={e} /> if you
            suspect someone else is using it.
          </p>

          <h2>4. Acceptable use</h2>
          <p>
            You will use {APP_NAME} lawfully, have a lawful basis to message the people you contact,
            and follow the rules of the messaging platforms you connect (including WhatsApp&rsquo;s
            Business and Commerce policies). You will not use it to send spam, or for any fraudulent
            or harmful purpose.
          </p>

          <h2>5. Fees and payment</h2>
          <p>
            The fees for your plan are set out at sign-up or in your order
            {e.uk ? ', and are exclusive of VAT, which we add at the applicable rate' : ''}. Unless
            stated otherwise they are billed monthly in advance and are non-refundable except where
            the law requires. We may change our fees on at least 30 days&rsquo; notice, taking
            effect from your next billing period. Messaging and telephony charges from third-party
            providers may be billed separately.
          </p>

          <h2>6. Third-party services</h2>
          <p>
            {APP_NAME} works with third parties such as WhatsApp, telephony providers, shops and
            payment providers. Your use of them is also governed by their terms, and we are not
            responsible for their availability or conduct.
          </p>

          <h2>7. Intellectual property</h2>
          <p>
            We, or our licensors, own the {APP_NAME} software and brand. You own your data and your
            conversations, and you give us the rights we need to process them to provide the
            service.
          </p>

          <h2>8. Data protection</h2>
          <p>
            Each of us will comply with{' '}
            {e.uk ? 'the UK GDPR and the Data Protection Act 2018' : 'the NDPA'}. Our{' '}
            <Link to="/privacy">Privacy Policy</Link> explains how we handle personal data as a
            controller. For your customers&rsquo; data you are the controller and we are your
            processor, and we will: process it only on your documented instructions, which these
            Terms record; bind everyone who handles it to confidentiality; keep it secure; use
            sub-processors only under contracts giving the same protection, telling you before we
            add or replace one; help you respond to people&rsquo;s requests; tell you without undue
            delay of a personal data breach; delete or return it when the service ends unless the
            law requires us to keep it; and give you the information you reasonably need to show
            these obligations are met.
          </p>

          <h2>9. Term and termination</h2>
          <p>
            You may cancel at any time; access continues until the end of the period you have paid
            for. We may suspend or end your account for material breach, non-payment or unlawful
            use. You may ask for an export of your data within 30 days of your account ending.
          </p>

          <h2>10. Liability</h2>
          <p>
            Except as these Terms say, and to the extent the law allows, the service is provided
            &ldquo;as is&rdquo;. Nothing in these Terms limits liability that cannot lawfully be
            limited
            {e.uk
              ? ', including for death or personal injury caused by negligence, or for fraud'
              : ''}
            . Subject to that, we are not liable for indirect or consequential loss, or for loss of
            profit, revenue, goodwill or data, and our total liability is limited to the fees you
            paid us in the 12 months before the claim arose.
          </p>

          <h2>11. Changes</h2>
          <p>
            We may update the service and these Terms. For significant changes we will update the
            effective date above and give you reasonable notice; using {APP_NAME} after a change
            takes effect means you accept it.
          </p>

          <h2>12. General and governing law</h2>
          <p>
            These Terms and our Privacy Policy are the entire agreement between us about the
            service. If any part is unenforceable, the rest stays in force.{' '}
            {e.uk
              ? 'No one else has any right under the Contracts (Rights of Third Parties) Act 1999 to enforce these Terms. They are governed by the law of England and Wales, and the courts of England and Wales have exclusive jurisdiction.'
              : 'These Terms are governed by the laws of the Federal Republic of Nigeria, and the Nigerian courts have jurisdiction over any dispute we cannot resolve informally.'}
          </p>

          <h2>13. Contact us</h2>
          <ContactBlock e={e} />
        </>
      )}
    </Layout>
  );
}
